import React, {
    FC,
    useState,
    useEffect,
    useRef,
    useCallback,
    HTMLAttributes,
    VideoHTMLAttributes,
    useImperativeHandle,
    ReactNode
} from 'react';
import styles from './index.module.less';
import cn from 'classnames';
import { getCdnUrl } from 'base/utils/trans-path';
import Hls from 'hls.js';
import { get375CssValue, getPercentage } from 'base/utils/dom-size';
import { isVideoPlaying } from 'base/utils/media';
// import { useStoreState } from 'base/store';
import { isVideoUrl } from 'base/utils/file';
import { IconPause, IconPlay, IconReplay } from '@base/icon';
import { useRequest } from 'ahooks';

export type TPlayingStatus = 'playing' | 'paused' | 'ended';

export type IControl = {
    size?: number;
    opacity?: number;
};

export type PureVideoProps = Omit<VideoHTMLAttributes<HTMLVideoElement>, 'src'> & {
    src: string | Array<string | void> | void;
    wrapProps?: HTMLAttributes<HTMLDivElement> & {
        'data-t-tar'?: string;
        'data-t-pos'?: string | number;
    };
    onPlay?: () => void;
    onPause?: () => void;
    onEnd?: () => void;
    control?: boolean | IControl; // 自定义控制器
    renderControl?: (props: {
        status: TPlayingStatus;
        size: string;
        buttonEL: ReactNode; // 控制器按钮元素
    }) => ReactNode; // 自定义控制器渲染函数
    autoPlay?: boolean;
    disablePlay?: boolean; // 仅展示封面，不播放（如：review列表中的视频）
    absolute?: boolean;
    canMultiplePlay?: boolean; // 可多个播放
    forceRender?: boolean; // src 为空时是否强制渲染video 标签，逐帧播放模块需要
    fit?: 'cover' | 'contain' | 'scale-down';
    width?: number;
    height?: number;
}

export type IPureVideoRef = {
    play: () => void;
    pause: () => void;
    reset: () => void;
    status: TPlayingStatus;
    getElements: () => {
        wrap: HTMLDivElement;
        video: HTMLVideoElement;
    }
}

type IControlBtnProps = {
    size: number;
    status: TPlayingStatus;
    isControlVisible?: boolean;
};

const VideoSource = (props: {
    src: string
}) => {
    const { src } = props;
    const pathname = src.replace(/[?#].*$/, '');
    return pathname.endsWith('.mp4')
    || pathname.endsWith('.webm')
    || pathname.endsWith('.mov') ? (
        <source
            src={getCdnUrl(src)}
            type={pathname.endsWith('.mp4')
                ? 'video/mp4'
                : (pathname.endsWith('.webm')
                        ? 'video/webm' : 'video/quicktime'
                )}
            data-wf-ignore="true"
        />
    ) : null;
}

const getM3u8Src = (src: string | string[]): string | undefined => {
    src = typeof src === 'string' ? src : src[0];
    return src.endsWith('.m3u8') ? src : void 0;
}

const controlAriaText: Record<TPlayingStatus, string> = {
    playing: 'Paused',
    paused: 'Play',
    ended: 'Replay',
};

export const Control: FC<{ status: TPlayingStatus, size: number }> = ({ status, size }) => {
    switch (status) {
        case 'playing':
            return <IconPause size={size}/>
        case 'paused':
            return <IconPlay size={size}/>
        case 'ended':
            return <IconReplay size={size}/>
    }
    return null;
};

const pauseAllVideos = (exceptVideo?: HTMLVideoElement) => {
    Array.prototype.forEach.call(document.getElementsByTagName('video'), video => {
        if (video !== exceptVideo && isVideoPlaying(video)) {
            video.pause();
        }
    })
};

const isPhone = process.env.NEXT_PUBLIC_TERMINAL === 'm';

function filterSrc(src: PureVideoProps['src']) {
    if (Array.isArray(src)) {
        src = src.filter(item => !!item && typeof item === 'string' && isVideoUrl(item));
        if (src.length > 0) {
            if (typeof src[0] === 'string' && src[0].replace(/[?#].*$/, '').endsWith('.m3u8')) {
                return src[0];
            }
            return src;
        }
    } else if (typeof src === 'string' && isVideoUrl(src)) {
        return src;
    }
    return '';
}

export const DEFAULT_CONTROL_SIZE = isPhone ? 62 : 80;

export const ControlBtn = React.forwardRef<HTMLButtonElement, IControlBtnProps>((props, ref) => {
    const { size, status, isControlVisible } = props;
    return (
        <button
            ref={ref}
            type="button"
            className={cn(
                styles['control-btn'],
                !isControlVisible && styles['control-btn-hide'],
                'btn-no-style g-control sw-video-control',
                `g-${status}`
            )}
            aria-label={controlAriaText[status]}
            style={{
                width: size,
                height: size,
                // opacity: controlConfig?.opacity,
                // opacity: status === 'playing' ? 0 : (controlConfig.opacity || 0.5),
            }}
        >
            <Control status={status} size={size}/>
        </button>
    )
});

export const PureVideo = React.forwardRef<IPureVideoRef, PureVideoProps>((props, ref) => {
    const {
        className,
        style,
        src: propsSrc,
        wrapProps,
        onPlay,
        onPause,
        onEnd,
        autoPlay: propsAutoPlay = false,
        disablePlay = false,
        controls = false,
        control: propsControl = false,
        renderControl,
        muted = false,
        loop = false,
        absolute = false,
        poster = '',
        canMultiplePlay = false,
        forceRender = false,
        fit = 'contain',
        width,
        height,
        ...videoProps
    } = props;

    const src = filterSrc(propsSrc) as string | string[];
    const statusRef = useRef<TPlayingStatus>('paused');
    const [status, setStatus] = useState<TPlayingStatus>('paused');

    const [isControlVisible, setIsControlVisible] = useState(true); // 控制播放暂停按钮可见状态
    const videoRef = useRef<HTMLVideoElement>(null);
    const wrapRef = useRef<HTMLDivElement>(null);
    const controlRef = useRef<HTMLButtonElement>(null);
    const playedRef = useRef<boolean>(false);// 手机pad未开始播放过的视频，点击空白区域不能触发播放，需要自定义事件

    const hideTimerRef = useRef<NodeJS.Timeout | null>(null); // 自动隐藏计时器引用
    // const { ua: { isMobile } } = useStoreState('rootStore');

    const control = controls ? false : propsControl; // 部分浏览器原生控件带中央播放按钮，所以原生和模拟播放控件只能显示一种
    const controllable = control || controls;
    const controlConfig = typeof control === 'object' ? control : {} as IControl;
    const controlSize = controlConfig.size || DEFAULT_CONTROL_SIZE;
    const iconSize = Math.round(controlSize * (isPhone ? (42 / 62) : (48 / 80)));
    const controlSizeValue = get375CssValue(controlSize);
    const autoPlay = propsAutoPlay && !disablePlay;

    const withSize = typeof width === 'number' && typeof height === 'number';

    const togglePlay = () => {
        if (disablePlay) {
            return;
        }
        const video = videoRef.current;
        if (video) {
            if (statusRef.current === 'playing') {
                video.pause();
            } else {
                video.play();
            }
        }
    };

    const handlePlay = () => {
        if (!canMultiplePlay) {
            pauseAllVideos(videoRef.current as HTMLVideoElement);
        }
        statusRef.current = 'playing';
        setStatus('playing');
        typeof onPlay === 'function' && onPlay();
        playedRef.current = true;
    };

    // const mobilePlay = () => {
    //     if (!playedRef.current) {
    //         setTimeout(() => {
    //             videoRef.current?.play();
    //         }, 100)
    //     } else {
    //         togglePlay();
    //     }
    // };

    const handlePause = () => {
        statusRef.current = 'paused';
        setStatus('paused');
        typeof onPause === 'function' && onPause();
    };
    const handleEnd = () => {
        statusRef.current = 'ended';
        setStatus('ended');
        typeof onEnd === 'function' && onEnd();
    };

    // 清除自动隐藏计时器
    const clearHideTimer = useCallback(() => {
        if (hideTimerRef.current) {
            clearTimeout(hideTimerRef.current);
            hideTimerRef.current = null;
        }
    }, []);

    const handleShowControlVisible = async () => {
        if (!control) {
            return;
        }
        if (status == 'playing') {
            setIsControlVisible(true)
            if (hideTimerRef.current) {
                clearHideTimer();
            }
            hideTimerRef.current = setTimeout(() => {
                setIsControlVisible(false)
            }, 2000);
        } else {
            hideTimerRef.current && clearHideTimer();
            setIsControlVisible(true)
        }
    }

    const { run: showControl } = useRequest(handleShowControlVisible, {
        // debounceWait: 300,
        throttleWait: 500,
        manual: true,
    })

    useImperativeHandle(ref, () => ({
        play: () => {
            if (statusRef.current !== 'playing') {
                videoRef.current?.play();
            }
        },
        pause: () => {
            if (statusRef.current === 'playing') {
                videoRef.current?.pause();
            }
        },
        reset: () => {
            if (videoRef.current) {
                videoRef.current.currentTime = 0;
            }
        },
        status: statusRef.current,
        getElements: () => {
            return {
                wrap: wrapRef.current as HTMLDivElement,
                video: videoRef.current as HTMLVideoElement,
            }
        }
    }));

    useEffect(() => {
        showControl()
    }, [status]);

    useEffect(() => {
        const video = videoRef.current;
        if (!video || !src) {
            return;
        }

        // m3u8 格式视频加载
        const m3u8Src = getM3u8Src(src);
        if (m3u8Src) {
            if (video.canPlayType('application/vnd.apple.mpegurl')) {
                // safari 支持 HLS
                video.src = getCdnUrl(m3u8Src);
            } else if (Hls.isSupported()) {
                // 其他浏览器使用 hls.js
                const hls = new Hls()
                hls.loadSource(getCdnUrl(m3u8Src));
                hls.attachMedia(video)
                video.controls = controls; // hls 加载视频后，controls 会被重置为 true
            } else {
                console.error('This is an old browser that does not support MSE https://developer.mozilla.org/en-US/docs/Web/API/Media_Source_Extensions_API')
            }
        }
    }, [src]);

    useEffect(() => {
        const video = videoRef.current;
        //绑定事件
        if (video && !disablePlay) {
            video.addEventListener('playing', handlePlay);
            video.addEventListener('pause', handlePause);
            video.addEventListener('ended', handleEnd);
            // 手机端原生播放器自带中间的播放按钮，手机端和 pc 模拟器表现不一样
            // 部分浏览器原生控件带中央播放按钮，禁止显示模拟控件
            // if (controls) {
            //     if (isMobile) {
            //         video.addEventListener('touchstart', mobilePlay);
            //     }
            // } else if (control) {
            if (control) {
                wrapRef.current!.addEventListener('click', togglePlay);
            }
        }
        return () => {
            if (!disablePlay) {
                video?.removeEventListener('playing', handlePlay);
                video?.removeEventListener('pause', handlePause);
                video?.removeEventListener('ended', handleEnd);

                // if (controls) {
                //     if (isMobile) {
                //         video?.addEventListener('touchstart', mobilePlay);
                //     }
                // } else if (control) {
                if (control) {
                    wrapRef.current?.addEventListener('click', togglePlay);
                }
            }
        }
    }, []);

    return !src && !forceRender ? null : (
        <div
            ref={wrapRef}
            className={cn(
                className,
                styles['pure-video'],
                'pure-video sw-video',
                { [`g-${status}`]: status === 'playing' },
                { pointer: control || controls },
                { 'sw-placeholder': withSize },
                absolute ? 'absolute' : 'relative',
            )}
            style={{
                paddingBottom: withSize ? getPercentage(width, height) : void 0,
                ...style
            }}
            {...wrapProps}
        >
            <video
                ref={videoRef}
                className={cn({
                        'g-uncontrollable': !controllable
                    },
                    `g-${fit}`,
                    { 'sw-overlay': withSize },
                )}
                autoPlay={autoPlay}
                muted={autoPlay || muted}
                preload={autoPlay ? 'auto' : 'metadata'}
                loop={loop}
                controls={controls}
                poster={poster ? getCdnUrl(poster) : poster}
                disablePictureInPicture
                controlsList="nodownload"
                data-wf-ignore="true"
                playsInline
                x5-playsinline="true"
                webkit-playsinline="true"
                {...(typeof src === 'string' ? {
                    src: src
                } : {})}
                {...videoProps}
                onMouseMove={(e) => {
                    showControl()
                    videoProps?.onMouseMove?.(e)
                }}
                onTouchMove={(e) => {
                    showControl()
                    videoProps?.onTouchMove?.(e)
                }}
            >
                {!getM3u8Src(src) && (
                    typeof src === 'string' ? (
                        <VideoSource src={src}/>
                    ) : src.map(item => (
                        <VideoSource key={item} src={item}/>
                    ))
                )}
                your browser does not support the video tag
            </video>

            {/*移动设备原生播放器自带中间的播放按钮*/}
            {control && typeof renderControl === 'function' && renderControl({
                status,
                size: controlSizeValue,
                buttonEL: (
                    <ControlBtn
                        isControlVisible={isControlVisible}
                        ref={controlRef}
                        status={status}
                        size={iconSize}
                    />
                )
            })}


            {control && typeof renderControl !== 'function' && (
                <ControlBtn
                    isControlVisible={isControlVisible}
                    ref={controlRef}
                    status={status}
                    size={iconSize}
                />
            )}
        </div>
    );

});

export default React.memo(PureVideo);